import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HandlerWithResult, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUrl } from "@insite/client-framework/ServerSideRendering";
import { Session } from "@insite/client-framework/Services/SessionService";
import { getSettingsCollection ,   getShouldLoadInventory,
    getShouldLoadPricing, } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandStateById } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrand from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrand";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { chain, PrepareStructuredPageData } from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/GenerateStructuredPageData";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

type HandlerType = HandlerWithResult<
    {
        product: ProductModel;
        productInfo?: ProductInfo;
        session: Session;
        brandId?: string | null;
        categoryIdWithBrandId?: string | null;
        categoryId?: string | null;
    },
    { structuredPageData: string }
>;

export const PrepareStructuredPageDataExtended: HandlerType = async props => {
    const {
        id,
        productTitle,
        smallImagePath,
        mediumImagePath,
        largeImagePath,
        canonicalUrl,
        manufacturerItem,
        detail,
        brand: productBrand,
        content,
    } = props.parameter.product;
    const state = props.getState();
    
     //   CENCK-538-PDP-VPSMissingProductJSON

    // Build absolute URL the same way CENCustomSchema did
    const url: { protocol: string; host: string } = IS_SERVER_SIDE ? getUrl()! : window.location;
    const authority = `${url.protocol}//${url.host}`;
    const absoluteCanonicalUrl =
        canonicalUrl && canonicalUrl.indexOf("http") === 0 ? canonicalUrl : authority + canonicalUrl;

    const data = {
        "@context": "https://schema.org",
        "@type": "Product",
        productID: id,
        name: productTitle,
        image: [smallImagePath, mediumImagePath, largeImagePath].filter(Boolean),
        mpn: manufacturerItem,
        sku: manufacturerItem, // customization: was detail?.sku
        url: absoluteCanonicalUrl, // customization: absolute instead of raw canonicalUrl
    } as any;

    // customization: description field, previously missing from this handler
    if (content?.htmlContent) {
        data.description = content.htmlContent
            .replace(/<[^>]+>/g, "")
            .replace(/[\r\n\t]/g, "")
            .trim();
    }

    const productInfo = props.parameter.productInfo;

       if (productInfo?.pricing && productInfo.inventory && productInfo.pricing.unitNetPrice > 0) {
            data.offers = {
                "@type": "Offer",
                availability: "https://schema.org/InStock",
                price: productInfo.pricing.unitNetPrice,
                priceCurrency: props.parameter.session?.currency?.currencyCode || "USD",
                url: absoluteCanonicalUrl,
            };
        }
    

    if (props.parameter.brandId) {
        let brandState = getBrandStateById(state, props.parameter.brandId);
        let brand = productBrand || brandState.value;
        if (!brand && !brandState.isLoading) {
            const awaitableLoadBrand = makeHandlerChainAwaitable(loadBrand);
            await awaitableLoadBrand({ brandId: props.parameter.brandId })(props.dispatch, props.getState);
            brandState = getBrandStateById(props.getState(), props.parameter.brandId);
            brand = brandState.value;
        }

        if (brand) {
            // data.brand = {
            //     "@type": "Brand",
            //     identifier: brand.id,
            //     name: brand.name,
            //     url: brand.detailPagePath,
            // };
            data.brand = {
                "@type": "Brand",
                name: brand.name,
            };
        }
    }

    if (props.parameter.categoryIdWithBrandId || props.parameter.categoryId) {
        const category = getCategoryState(
            state,
            props.parameter.categoryIdWithBrandId || props.parameter.categoryId || "",
        ).value;

        if (category) {
            data.category = category.path;
        }
    }

    props.result = { structuredPageData: JSON.stringify(data) };
};

replaceInChain(chain, PrepareStructuredPageData, PrepareStructuredPageDataExtended);
